import React from 'react';
import { User, Users } from '@phosphor-icons/react';

interface ReservationBadgeProps {
  reservation: any;
  type: 'checkin' | 'checkout' | 'stay';
  onReservationClick: (reservation: any) => void;
  hoveredStayCell?: string | null;
  cellId?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

interface ReservationContainerProps {
  children: React.ReactNode;
  alignment: 'start' | 'center' | 'end';
}

// Helper function to get reservation status color
const getReservationStatusClass = (reservation: any) => {
  const today = new Date().toISOString().slice(0, 10);
  const checkinDate = new Date(reservation.checkin_field || reservation.check_in_default).toISOString().slice(0, 10);
  const checkoutDate = new Date(reservation.checkout_field || reservation.check_out_default).toISOString().slice(0, 10);
  
  if (today >= checkinDate && today <= checkoutDate) {
    return 'reservation-current';
  } else if (today > checkoutDate) {
    return 'reservation-past';
  } else {
    return 'reservation-future';
  }
};


// Reservation Container Component
export const ReservationContainer: React.FC<ReservationContainerProps> = ({ 
  children, 
  alignment 
}) => {
  const alignmentClass = `reservation-container-${alignment}`;
  
  return (
    <div className={`reservation-container ${alignmentClass}`}>
      {children}
    </div>
  );
};

// Single Reservation Badge Component
export const ReservationBadge: React.FC<ReservationBadgeProps> = ({ 
  reservation, 
  type, 
  onReservationClick,
  hoveredStayCell,
  cellId,
  onMouseEnter,
  onMouseLeave
}) => {
  const statusClass = getReservationStatusClass(reservation);
  const isHovered = hoveredStayCell === cellId;
  
  const getBadgeClasses = () => {
    const baseClasses = ['reservation-badge'];
    
    switch (type) {
      case 'checkin':
        baseClasses.push('reservation-badge-half', 'reservation-checkin');
        break;
      case 'checkout':
        baseClasses.push('reservation-badge-half', 'reservation-checkout');
        break;
      case 'stay':
        baseClasses.push('reservation-badge-full');
        if (isHovered) {
          baseClasses.push('reservation-hovered-stay');
        }
        break;
    }
    
    baseClasses.push(statusClass);
    return baseClasses.join(' ');
  };

  const handleClick = () => {
    onReservationClick(reservation);
  };

  const guestName = reservation.guest_name || 'Guest';

  return (
    <div 
      className={getBadgeClasses()}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {type === 'stay' ? (
        <span className="reservation-guest-name">
          {guestName}
        </span>
      ) : (
        guestName
      )}
    </div>
  );
};

// Stacked Reservations Component
export const StackedReservationBadge: React.FC<{
  reservations: any[];
  type: 'checkin' | 'checkout' | 'stay';
  onReservationClick: (reservation: any) => void;
  hoveredStayCell?: string | null;
  cellId?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  bubbleInstance?: any;
  onTooltipHover?: (reservations: any[], type: string, show: boolean, event?: React.MouseEvent) => void;
  }> = ({ 
    reservations, 
    type, 
    onReservationClick,
    hoveredStayCell,
    cellId,
    onMouseEnter,
    onMouseLeave,
    bubbleInstance,
    onTooltipHover
  }) => {
  if (reservations.length === 0) return null;

  const count = reservations.length;
  const primaryReservation = reservations[0];
  const statusClass = getReservationStatusClass(primaryReservation);
  const isHovered = hoveredStayCell === cellId;
  
  const getBadgeClasses = () => {
    const baseClasses = ['reservation-badge', 'stacked-reservation'];
    
    switch (type) {
      case 'checkin':
        baseClasses.push('reservation-badge-half', 'reservation-checkin');
        break;
      case 'checkout':
        baseClasses.push('reservation-badge-half', 'reservation-checkout');
        break;
      case 'stay':
        baseClasses.push('reservation-badge-full');
        if (isHovered) {
          baseClasses.push('reservation-hovered-stay');
        }
        break;
    }
    
    baseClasses.push(statusClass);
    return baseClasses.join(' ');
  };

  const handleClick = () => {
    onReservationClick(count > 1 ? reservations : primaryReservation);
  };

  const guestName = primaryReservation.guest_name || 'Guest';
  const displayText = count > 1 ? count.toString() : guestName;

  // Get reservation status for display
  const getReservationStatus = (reservation: any) => {
    const today = new Date().toISOString().slice(0, 10);
    const checkinDate = new Date(reservation.checkin_field || reservation.check_in_default).toISOString().slice(0, 10);
    const checkoutDate = new Date(reservation.checkout_field || reservation.check_out_default).toISOString().slice(0, 10);
    
    if (today >= checkinDate && today <= checkoutDate) {
      return 'in-stay';
    } else if (today > checkoutDate) {
      return 'past';
    } else {
      return 'future';
    }
  };

  const status = getReservationStatus(primaryReservation);

  const handleMouseEnter = (event: React.MouseEvent) => {
    if (onTooltipHover) {
      onTooltipHover(reservations, type, true, event);
    }
    if (onMouseEnter) {
      onMouseEnter();
    }
  };

  const handleMouseLeave = (event: React.MouseEvent) => {
    if (onTooltipHover) {
      onTooltipHover([], type, false, event);
    }
    if (onMouseLeave) {
      onMouseLeave();
    }
  };

  // User icon components
  const SingleUserIcon = () => (
    <User size={14} style={{ opacity: 0.8 }} />
  );

  const MultipleUserIcon = () => (
    <Users size={14} style={{ opacity: 0.8 }} />
  );

  return (
    <div 
      className={getBadgeClasses()}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {count > 1 ? (
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '2px' }}>
          <MultipleUserIcon />
          <div style={{ fontSize: '9px' }}>{count} reservations</div>
        </div>
      ) : type === 'stay' ? (
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '2px' }}>
          <span className="reservation-guest-icon">
            <SingleUserIcon />
          </span>
          <span className="reservation-guest-name" style={{ fontSize: '9px' }}>
            {guestName}
          </span>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '2px' }}>
          <SingleUserIcon />
          <div style={{ fontSize: '9px' }}>{guestName}</div>
        </div>
      )}
    </div>
  );
};

// Full-Width Stacked Reservations for Complex Same-Day Scenarios
export const FullWidthStackedReservations: React.FC<{
  allReservations: any[];
  onReservationClick: (reservation: any) => void;
  hoveredStayCell?: string | null;
  cellId?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  bubbleInstance?: any;
  onTooltipHover?: (reservations: any[], type: string, show: boolean, event?: React.MouseEvent) => void;
}> = ({ 
  allReservations, 
  onReservationClick,
  hoveredStayCell,
  cellId,
  onMouseEnter,
  onMouseLeave,
  bubbleInstance,
  onTooltipHover
}) => {
  if (allReservations.length === 0) return null;

  return (
    <ReservationContainer alignment="center">
      <StackedReservationBadge
        reservations={allReservations}
        type="stay"
        onReservationClick={onReservationClick}
        hoveredStayCell={hoveredStayCell}
        cellId={cellId}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        bubbleInstance={bubbleInstance}
        onTooltipHover={onTooltipHover}
      />
    </ReservationContainer>
  );
};

// Combined Same-Day Reservations Component
export const SameDayReservations: React.FC<{
  checkinReservations: any[];
  checkoutReservations: any[];
  onReservationClick: (reservation: any) => void;
  bubbleInstance?: any;
  onTooltipHover?: (reservations: any[], type: string, show: boolean, event?: React.MouseEvent) => void;
}> = ({ checkinReservations, checkoutReservations, onReservationClick, bubbleInstance, onTooltipHover }) => {
  if (checkinReservations.length === 0 || checkoutReservations.length === 0) {
    return null;
  }

  return (
    <ReservationContainer alignment="center">
      {/* Checkout reservations on left (50% width) */}
      <StackedReservationBadge
        reservations={checkoutReservations}
        type="checkout"
        onReservationClick={onReservationClick}
        bubbleInstance={bubbleInstance}
        onTooltipHover={onTooltipHover}
      />
      {/* Checkin reservations on right (50% width) */}
      <StackedReservationBadge
        reservations={checkinReservations}
        type="checkin"
        onReservationClick={onReservationClick}
        bubbleInstance={bubbleInstance}
        onTooltipHover={onTooltipHover}
      />
    </ReservationContainer>
  );
};

// Individual Reservation Components
export const CheckinOnlyReservation: React.FC<{
  reservations: any[];
  onReservationClick: (reservation: any) => void;
  bubbleInstance?: any;
  onTooltipHover?: (reservations: any[], type: string, show: boolean, event?: React.MouseEvent) => void;
}> = ({ reservations, onReservationClick, bubbleInstance, onTooltipHover }) => {
  if (reservations.length === 0) return null;

  return (
    <ReservationContainer alignment="end">
      <StackedReservationBadge
        reservations={reservations}
        type="checkin"
        onReservationClick={onReservationClick}
        bubbleInstance={bubbleInstance}
        onTooltipHover={onTooltipHover}
      />
    </ReservationContainer>
  );
};

export const CheckoutOnlyReservation: React.FC<{
  reservations: any[];
  onReservationClick: (reservation: any) => void;
  bubbleInstance?: any;
  onTooltipHover?: (reservations: any[], type: string, show: boolean, event?: React.MouseEvent) => void;
}> = ({ reservations, onReservationClick, bubbleInstance, onTooltipHover }) => {
  if (reservations.length === 0) return null;

  return (
    <ReservationContainer alignment="start">
      <StackedReservationBadge
        reservations={reservations}
        type="checkout"
        onReservationClick={onReservationClick}
        bubbleInstance={bubbleInstance}
        onTooltipHover={onTooltipHover}
      />
    </ReservationContainer>
  );
};

export const StayReservation: React.FC<{
  reservations: any[];
  onReservationClick: (reservation: any) => void;
  hoveredStayCell: string | null;
  cellId: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  bubbleInstance?: any;
  onTooltipHover?: (reservations: any[], type: string, show: boolean, event?: React.MouseEvent) => void;
}> = ({ 
  reservations, 
  onReservationClick, 
  hoveredStayCell, 
  cellId, 
  onMouseEnter, 
  onMouseLeave,
  bubbleInstance,
  onTooltipHover
}) => {
  if (reservations.length === 0) return null;

  return (
    <ReservationContainer alignment="center">
      <StackedReservationBadge
        reservations={reservations}
        type="stay"
        onReservationClick={onReservationClick}
        hoveredStayCell={hoveredStayCell}
        cellId={cellId}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        bubbleInstance={bubbleInstance}
        onTooltipHover={onTooltipHover}
      />
    </ReservationContainer>
  );
};

// Export helper function
export { getReservationStatusClass };